/** Sentinel id for the synthetic "radio" track (not in the catalog). */
export const RADIO_TRACK_SENTINEL_ID = -1;

export type RadioScheduleItem = { time: string; show: string; host: string };

export type StreamUrlType = 'hls' | 'mp3' | 'youtube' | 'soundcloud' | 'icecast' | 'unknown' | 'none';

export interface RadioConfig {
  stationName: string;
  tagline: string;
  /** Display-only listener count on the card */
  listenerCount: number;
  /** Whether the station is currently broadcasting (shows pulsing green dot) */
  isLive: boolean;
  /** Optional real audio stream URL (HLS .m3u8, direct MP3, Icecast, etc.) */
  streamUrl: string;
  currentShowTitle: string;
  currentShowSubtitle: string;
  trackTitle: string;
  trackArtist: string;
  trackAlbum: string;
  trackDuration: string;
  trackImage: string;
  schedule: RadioScheduleItem[];
}

export const DEFAULT_RADIO_CONFIG: RadioConfig = {
  stationName: 'Frequency Locus Radio One',
  tagline: 'A steady stream of African music, warm curation, and live atmosphere throughout the day.',
  listenerCount: 2847,
  isLive: true,
  streamUrl: '',
  currentShowTitle: 'African Vibes Live',
  currentShowSubtitle: 'Streaming live with curated energy from across the continent.',
  trackTitle: 'Ye',
  trackArtist: 'Burna Boy',
  trackAlbum: 'Outside',
  trackDuration: '2:48',
  trackImage:
    'https://readdy.ai/api/search-image?query=African%20music%20album%20cover%20with%20vibrant%20orange%20and%20red%20colors%2C%20traditional%20African%20patterns%2C%20modern%20design%2C%20music%20notes%2C%20isolated%20on%20dark%20background%2C%20centered%20composition&width=300&height=300&seq=track2&orientation=squarish',
  schedule: [
    { time: '06:00 - 09:00', show: 'Morning Beats', host: 'DJ Kemi' },
    { time: '09:00 - 12:00', show: 'African Vibes Live', host: 'DJ Tunde' },
    { time: '12:00 - 15:00', show: 'Afrobeats Hour', host: 'DJ Amara' },
    { time: '15:00 - 18:00', show: 'Classic Hits', host: 'DJ Kofi' },
    { time: '18:00 - 21:00', show: 'Evening Mix', host: 'DJ Zara' },
    { time: '21:00 - 00:00', show: 'Night Grooves', host: 'DJ Seun' },
  ],
};

export function mergeRadioConfig(raw: unknown): RadioConfig {
  const d = DEFAULT_RADIO_CONFIG;
  if (!raw || typeof raw !== 'object') return { ...d };
  const o = raw as Record<string, unknown>;
  const schedule = Array.isArray(o.schedule)
    ? (o.schedule as RadioScheduleItem[])
        .filter((x) => x && typeof x === 'object')
        .map((x) => ({
          time: String((x as RadioScheduleItem).time ?? ''),
          show: String((x as RadioScheduleItem).show ?? ''),
          host: String((x as RadioScheduleItem).host ?? ''),
        }))
    : d.schedule;
  return {
    stationName: typeof o.stationName === 'string' ? o.stationName : d.stationName,
    tagline: typeof o.tagline === 'string' ? o.tagline : d.tagline,
    listenerCount: typeof o.listenerCount === 'number' && isFinite(o.listenerCount) ? Math.max(0, o.listenerCount) : d.listenerCount,
    isLive: typeof o.isLive === 'boolean' ? o.isLive : d.isLive,
    streamUrl: typeof o.streamUrl === 'string' ? o.streamUrl : d.streamUrl,
    currentShowTitle: typeof o.currentShowTitle === 'string' ? o.currentShowTitle : d.currentShowTitle,
    currentShowSubtitle: typeof o.currentShowSubtitle === 'string' ? o.currentShowSubtitle : d.currentShowSubtitle,
    trackTitle: typeof o.trackTitle === 'string' ? o.trackTitle : d.trackTitle,
    trackArtist: typeof o.trackArtist === 'string' ? o.trackArtist : d.trackArtist,
    trackAlbum: typeof o.trackAlbum === 'string' ? o.trackAlbum : d.trackAlbum,
    trackDuration: typeof o.trackDuration === 'string' ? o.trackDuration : d.trackDuration,
    trackImage: typeof o.trackImage === 'string' ? o.trackImage : d.trackImage,
    schedule: schedule.length ? schedule : d.schedule,
  };
}

/** Detect the type of a stream/audio URL for display purposes. */
export function detectStreamType(url: string): StreamUrlType {
  if (!url || !url.trim()) return 'none';
  const u = url.trim().toLowerCase();
  if (u.includes('.m3u8')) return 'hls';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('soundcloud.com')) return 'soundcloud';
  if (u.includes('.mp3') || u.includes('.ogg') || u.includes('.aac') || u.includes('.opus')) return 'mp3';
  if (u.includes(':8000') || u.includes(':8080') || u.includes('icecast') || u.includes('/stream')) return 'icecast';
  return 'unknown';
}

/**
 * Given the schedule rows, return the index of the slot whose time range covers
 * now (or the provided minutes-since-midnight). Returns -1 if no match.
 * Parses formats like "06:00 - 09:00" or "06:00–09:00".
 */
export function detectActiveScheduleSlot(schedule: RadioScheduleItem[], nowMinutes?: number): number {
  const now = nowMinutes ?? (new Date().getHours() * 60 + new Date().getMinutes());
  for (let i = 0; i < schedule.length; i++) {
    const match = schedule[i].time.match(/(\d{1,2}):(\d{2})\s*[-\u2013]\s*(\d{1,2}):(\d{2})/);
    if (!match) continue;
    const startMin = parseInt(match[1]) * 60 + parseInt(match[2]);
    let endMin = parseInt(match[3]) * 60 + parseInt(match[4]);
    if (endMin === 0) endMin = 24 * 60; // midnight wrap
    if (now >= startMin && now < endMin) return i;
  }
  return -1;
}
